import React, { Component } from 'react';
import PropTypes from 'prop-types';

import styled from 'styled-components';
import { SearchBar } from '../';
import { colors } from '../theme';

//Minimum width for showing the focused search field inline (instead of in white overlay)
const MIN_WIDTH_INLINE_SEARCH = '1500px';

const SearchWrapper = styled.div`
  flex: 1;
  position: initial;

  @media (min-width: ${() => MIN_WIDTH_INLINE_SEARCH}) {
    position: relative;
  }
`;

const FormPlaceholder = styled.div`
  padding: 10px 17px;
  height: 40px;
  background-color: ${colors.LIGHT_GREY};
  border-radius: 4px;
  cursor: pointer;
  z-index: ${props => (props.open ? -1 : 2)};

  @media (min-width: ${props => props.theme.breakpoints.md}rem) {
    padding: 10px 20px;
    text-align: center;
  }

  svg {
    vertical-align: middle;
    margin-right: 10px;
  }

  span {
    margin: 0 auto;
    color: ${colors.MEDIUM_DARK_GREY};
    font-size: 13px;

    @media (min-width: ${props => props.theme.breakpoints.md}rem) {
      font-size: 14px;
    }
  }
`;

const SearchForm = styled.div`
  display: inline-block;
  width: 100%;
  top: 0;
  left: 0;

  transition: opacity 0.5s ease-in-out;
  opacity: ${props => (props.open ? 1 : 0)};
  z-index: ${props => (props.open ? 2 : -1)};
  position: absolute;
  background: ${colors.WHITE};

  padding: 15px 40px;
  box-shadow: 0 6px 3px -3px rgba(0, 0, 0, 0.02);

  .search-close-button {
    position: absolute;
    top: 26px;
    left: 12px;
    z-index: 1;
  }

  form {
    max-width: 700px;
    margin: 0 auto;
    width: 100%;

    .search-container {
      padding: 0;
    }

    input {
      padding: 7px 7px 7px 40px;
      font-size: 14px;
      ::-webkit-input-placeholder {
        font-size: 14px;
      }
    }

    .search-icon {
      top: 13px;
    }

    button {
      padding: 11px 0;
      margin-bottom: 0;
    }
  }

  @media (min-width: ${() => MIN_WIDTH_INLINE_SEARCH}) {
    background: none;
    box-shadow: none;
    padding: 0;
    width: 134%;
    margin: 0 -17%;

    form {
      max-width: 100%;
    }

    .search-close-button {
      display: none;
    }
  }
`;

const suggestionTypes = {
  'Let us find you': 'Post Job',
  'Browse ': 'Directory'
};

const getSuggestionType = suggestion => suggestionTypes[suggestion.prefix_text] || 'Business';

export default class HeaderSearch extends Component {
  static propTypes = {
    fetchUserInfo: PropTypes.func,
    loginStatus: PropTypes.string,
    loginInfo: PropTypes.object,
    initialSuggestion: PropTypes.string,
    initialLocation: PropTypes.string,
    trackEvent: PropTypes.func
  };

  state = {
    isOpen: false
  };

  /* used for detecting clicks outside search bar component - shearch should close (revert to
  grey bar) if a user clicks anywhere outside the search bar
   */
  componentWillMount() {
    if (typeof document !== 'undefined') {
      document.addEventListener('click', this.shouldSearchClose);
    }
  }

  // new in react 16, stops errors in the header crashing the site. If there is an issue, just close the header
  componentDidCatch(error) {
    this.setState({ isOpen: false });
    if (typeof newrelic !== 'undefined') {
      window.newrelic.noticeError(error);
    }
  }

  componentWillUnmount() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this.shouldSearchClose);
    }
  }

  shouldSearchClose = e => {
    /*If a click was detected and it was not inside the search bar component or on the
    autosuggest dropdown options, close the search bar.

    Need to use closest(selector) to identify the autocomplete suggestions, cause once you click them
    they are hidden/removed from inside search bar component (so the first condition wasn't working as expected.)
    Kinda dirty, feel free to find a cleaner way to do this :)
     */

    if (!this.node.contains(e.target) && !e.target.closest("[class*='suggestions-list']")) {
      this.close();
    }
  };

  openSearch = () => {
    this.setState({ isOpen: true });
    this.props.trackEvent && this.props.trackEvent('Header', 'Open Search Bar');
    document.querySelector('.header-search-bar #category-search').focus();
  };

  close = () => {
    this.setState({ isOpen: false });
  };

  render() {
    const { isOpen } = this.state;
    const { initialSuggestion, initialLocation, trackEvent } = this.props;
    return (
      <div ref={node => (this.node = node)}>
        <SearchWrapper onMouseDown={this.shouldSearchClose}>
          <FormPlaceholder open={isOpen} selected={isOpen} onClick={this.openSearch}>
            <svg
              width="13px"
              height="13px"
              viewBox="0 0 13 13"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs />
              <g id="03" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                <g id="B03" transform="translate(-637.000000, -29.000000)" fill="#8E8F91">
                  <g id="Group-24" transform="translate(495.000000, 15.000000)">
                    <g id="Group-12" transform="translate(142.000000, 8.000000)">
                      <path
                        d="M8.99999994,11.5 C8.99999994,10.5364535 8.65755556,9.71224301 7.97265625,9.02734375 C7.28775694,8.3424445 6.46354638,8 5.50000019,8 C4.53645319,8 3.71224263,8.3424445 3.02734413,9.02734375 C2.34244481,9.71224301 1.99999963,10.5364535 1.99999963,11.5 C1.99999963,12.4635465 2.34244481,13.287757 3.02734413,13.9726563 C3.71224263,14.6575555 4.53645319,15 5.50000019,15 C6.46354638,15 7.28775694,14.6575555 7.97265625,13.9726563 C8.65755556,13.287757 8.99999994,12.4635465 8.99999994,11.5 L8.99999994,11.5 L8.99999994,11.5 L8.99999994,11.5 Z M13,18 C13,18.2708347 12.9010424,18.5052073 12.7031247,18.703125 C12.505207,18.9010426 12.2708349,19 12.0000002,19 C11.7187484,19 11.4843763,18.9010426 11.2968749,18.703125 L8.61718731,16.03125 C7.6848915,16.6770865 6.64583887,17 5.50000019,17 C4.755205,17 4.04297237,16.8554702 3.36328119,16.5664063 C2.68359,16.2773423 2.09765888,15.8867212 1.60546912,15.3945312 C1.11327856,14.9023413 0.722657812,14.3164096 0.433593875,13.6367188 C0.144529938,12.9570279 0,12.2447954 0,11.5 C0,10.7552046 0.144529938,10.0429721 0.433593875,9.36328125 C0.722657812,8.68359035 1.11327856,8.09765871 1.60546912,7.60546875 C2.09765888,7.11327879 2.68359,6.7226577 3.36328119,6.43359375 C4.04297237,6.14452981 4.755205,6 5.50000019,6 C6.24479538,6 6.957028,6.14452981 7.63671837,6.43359375 C8.31640956,6.7226577 8.9023415,7.11327879 9.39453125,7.60546875 C9.886721,8.09765871 10.2773426,8.68359035 10.5664065,9.36328125 C10.8554704,10.0429721 11.0000004,10.7552046 11.0000004,11.5 C11.0000004,12.6458391 10.6770869,13.6848912 10.0312501,14.6171875 L12.7109377,17.296875 C12.9036464,17.4895843 13,17.723957 13,18 L13,18 L13,18 L13,18 Z"
                        id="Shape"
                        transform="translate(6.500000, 12.500000) scale(-1, 1) translate(-6.500000, -12.500000) "
                      />
                    </g>
                  </g>
                </g>
              </g>
            </svg>
            <span>
              {initialSuggestion
                ? `${initialSuggestion.practice_person_plural}  ${initialLocation &&
                    ` in ${initialLocation.suburb}`}`
                : 'Any trades in Australia'}
            </span>
          </FormPlaceholder>

          <SearchForm open={isOpen}>
            <span className="hui-icon-close search-close-button" onClick={this.close} />

            <SearchBar
              className="header-search-bar"
              categoryPlaceholder="Eg. Plumbers, Electricians"
              postcodePlaceholder="Enter Postcode"
              buttonText="Go"
              initialSuggestion={initialSuggestion}
              initialLocation={initialLocation}
              fontSize={14}
              showBorder={false}
              onSuggestionSelected={(suggestion, suggestionValue) =>
                trackEvent(
                  'Header',
                  `Select ${getSuggestionType(suggestion)} Search Result`,
                  suggestionValue
                )
              }
              onSubmit={url => {
                trackEvent('Header', 'Submit Search', url);
                window.location = url;
              }}
              onFocusLocation={() => {
                trackEvent('Header', 'Focus Location Autocompelete');
              }}
              onBlurLocation={() => trackEvent('Header', 'Blur Location Autocompelete')}
              onFocusAutosuggest={() => {
                trackEvent('Header', 'Focus Suggest Autocompelete');
              }}
              onBlurAutosuggest={() => trackEvent('Header', 'Blur Suggest Autocompelete')}
            />
          </SearchForm>
        </SearchWrapper>
      </div>
    );
  }
}
